/**
 * ProfileBuilder - Visual drag-and-drop standards profile builder (T7.5)
 *
 * Three-column layout: Available → Included (with priority slider) → Deprioritized.
 * Uses @dnd-kit/core for drag-drop interactions.
 * Foundation standards cannot be deprioritized (enforced visually).
 *
 * Patent: Standards Profile Builder for AI-Governed Systems
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 */

import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { FiAlertTriangle, FiShield } from 'react-icons/fi';
import { STANDARD_CATEGORIES, type CreateProfileInput } from '../../api/standardsProfiles';

const FOUNDATION_CATEGORIES = ['serverless-saas-aws', 'multi-agent-orchestration', 'compliance-security', 'security'];

interface ProfileBuilderProps {
  categoryIncludes: string[];
  categoryDeprioritizes: string[];
  priorityOverrides: Record<string, number>;
  onChange: (updates: Partial<Pick<CreateProfileInput, 'category_includes' | 'category_deprioritizes' | 'priority_overrides'>>) => void;
}

// Draggable category card
const CategoryCard: React.FC<{
  category: string;
  priority?: number;
  onPriorityChange?: (value: number) => void;
  isFoundation?: boolean;
  isDragging?: boolean;
}> = ({ category, priority, onPriorityChange, isFoundation, isDragging }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: category,
    data: { category },
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.5 : 1 }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`p-3 rounded-lg border-2 cursor-grab active:cursor-grabbing transition-shadow
        ${isFoundation ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}
        hover:shadow-md`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isFoundation && <FiShield className="h-4 w-4 text-amber-600" />}
          <span className="text-sm font-medium text-gray-900">
            {category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </span>
        </div>
        {isFoundation && (
          <span className="text-xs px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded">Foundation</span>
        )}
      </div>

      {/* Priority slider (only when included) */}
      {onPriorityChange && (
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Priority</span>
            <span className="font-mono">{priority ?? 15}</span>
          </div>
          <input
            type="range"
            min={isFoundation ? 10 : 1}
            max={30}
            value={priority ?? 15}
            onChange={(e) => onPriorityChange(parseInt(e.target.value))}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            onPointerDown={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

// Droppable zone
const DropZone: React.FC<{
  id: string;
  title: string;
  description: string;
  children: React.ReactNode;
  color: string;
}> = ({ id, title, description, children, color }) => {
  const { isOver, setNodeRef } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 min-w-0 rounded-lg border-2 border-dashed p-3 transition-colors min-h-[200px]
        ${isOver ? `${color} border-solid` : 'border-gray-300 bg-gray-50'}`}
    >
      <h4 className="text-sm font-semibold text-gray-700 mb-1">{title}</h4>
      <p className="text-xs text-gray-500 mb-3">{description}</p>
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
};

const ProfileBuilder: React.FC<ProfileBuilderProps> = ({
  categoryIncludes,
  categoryDeprioritizes,
  priorityOverrides,
  onChange,
}) => {
  const [activeDrag, setActiveDrag] = useState<string | null>(null);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  const available = STANDARD_CATEGORIES.filter(
    c => !categoryIncludes.includes(c) && !categoryDeprioritizes.includes(c)
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDrag(event.active.id as string);
    setConflictMessage(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDrag(null);

    if (!over) return;

    const category = active.id as string;
    const targetZone = over.id as string;
    const isFoundation = FOUNDATION_CATEGORIES.includes(category);

    // Foundation categories cannot be deprioritized
    if (targetZone === 'deprioritized' && isFoundation) {
      setConflictMessage(`"${category}" is a foundation standard and cannot be deprioritized.`);
      return;
    }

    // Remove from current location
    const newIncludes = categoryIncludes.filter(c => c !== category);
    const newDeprioritizes = categoryDeprioritizes.filter(c => c !== category);
    const newOverrides = { ...priorityOverrides };

    // Add to target zone
    if (targetZone === 'included') {
      newIncludes.push(category);
      if (!newOverrides[category]) {
        newOverrides[category] = isFoundation ? 10 : 15;
      }
    } else if (targetZone === 'deprioritized') {
      newDeprioritizes.push(category);
      delete newOverrides[category];
    } else if (targetZone === 'available') {
      delete newOverrides[category];
    }

    onChange({
      category_includes: newIncludes,
      category_deprioritizes: newDeprioritizes,
      priority_overrides: newOverrides,
    });
  };

  const handlePriorityChange = (category: string, value: number) => {
    onChange({
      priority_overrides: { ...priorityOverrides, [category]: value },
    });
  };

  return (
    <div className="space-y-3">
      {/* Conflict warning */}
      {conflictMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <FiAlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{conflictMessage}</p>
          <button
            onClick={() => setConflictMessage(null)}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            ✕
          </button>
        </div>
      )}

      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex flex-col md:flex-row gap-3">
          {/* Available */}
          <DropZone
            id="available"
            title="Available"
            description="Drag categories to include or deprioritize"
            color="bg-gray-100"
          >
            {available.map(cat => (
              <CategoryCard
                key={cat}
                category={cat}
                isFoundation={FOUNDATION_CATEGORIES.includes(cat)}
                isDragging={activeDrag === cat}
              />
            ))}
            {available.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">All categories assigned</p>
            )}
          </DropZone>

          {/* Included */}
          <DropZone
            id="included"
            title="Included (Boosted)"
            description="Higher priority in delivery ordering"
            color="bg-green-50 border-green-300"
          >
            {categoryIncludes.map(cat => (
              <CategoryCard
                key={cat}
                category={cat}
                priority={priorityOverrides[cat] ?? 15}
                onPriorityChange={(val) => handlePriorityChange(cat, val)}
                isFoundation={FOUNDATION_CATEGORIES.includes(cat)}
                isDragging={activeDrag === cat}
              />
            ))}
            {categoryIncludes.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">Drop categories here to boost</p>
            )}
          </DropZone>

          {/* Deprioritized */}
          <DropZone
            id="deprioritized"
            title="Deprioritized"
            description="Lower priority (not removed)"
            color="bg-orange-50 border-orange-300"
          >
            {categoryDeprioritizes.map(cat => (
              <CategoryCard
                key={cat}
                category={cat}
                isFoundation={FOUNDATION_CATEGORIES.includes(cat)}
                isDragging={activeDrag === cat}
              />
            ))}
            {categoryDeprioritizes.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-4">Drop non-foundation categories here</p>
            )}
          </DropZone>
        </div>

        <DragOverlay>
          {activeDrag ? (
            <div className="p-3 rounded-lg border-2 border-indigo-400 bg-indigo-50 shadow-lg">
              <span className="text-sm font-medium text-indigo-900">
                {activeDrag.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default ProfileBuilder;
